import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

export interface SubscriptionPlanDB {
  id: string;
  name: string;
  tier: 'free' | 'starter' | 'pro' | 'unlimited';
  price: number;
  price_display: string;
  description: string | null;
  features: string[];
  lead_limit: number;
  is_recommended: boolean;
  is_active: boolean;
  duration_days: number;
  created_at: string;
  updated_at: string;
}

// Helper to convert DB response to typed interface
const toSubscriptionPlanDB = (data: any): SubscriptionPlanDB => ({
  id: data.id,
  name: data.name,
  tier: data.tier,
  price: data.price,
  price_display: data.price_display,
  description: data.description,
  features: Array.isArray(data.features) ? data.features : [],
  lead_limit: data.lead_limit,
  is_recommended: data.is_recommended,
  is_active: data.is_active,
  duration_days: data.duration_days,
  created_at: data.created_at,
  updated_at: data.updated_at,
});

export interface CreateSubscriptionPlanInput {
  name: string;
  tier: 'free' | 'starter' | 'pro' | 'unlimited';
  price: number;
  price_display: string;
  description?: string;
  features: string[];
  lead_limit: number;
  is_recommended?: boolean;
  is_active?: boolean;
  duration_days?: number;
}

export interface UpdateSubscriptionPlanInput extends Partial<CreateSubscriptionPlanInput> {
  id: string;
}

// Get all subscription plans (admin view)
export const getAllSubscriptionPlans = async (): Promise<SubscriptionPlanDB[]> => {
  const { data, error } = await supabase
    .from('subscription_plans')
    .select('*')
    .order('price', { ascending: true });

  if (error) {
    console.error('Error fetching subscription plans:', error);
    toast({
      title: "Error",
      description: "Failed to fetch subscription plans",
      variant: "destructive",
    });
    throw error;
  }

  return (data || []).map(toSubscriptionPlanDB);
};

// Get active subscription plans (customer view)
export const getActiveSubscriptionPlans = async (): Promise<SubscriptionPlanDB[]> => {
  const { data, error } = await supabase
    .from('subscription_plans')
    .select('*')
    .eq('is_active', true)
    .order('price', { ascending: true });

  if (error) {
    console.error('Error fetching active subscription plans:', error);
    throw error;
  }

  return (data || []).map(toSubscriptionPlanDB);
};

// Get subscription plan by ID
export const getSubscriptionPlanById = async (id: string): Promise<SubscriptionPlanDB | null> => {
  const { data, error } = await supabase
    .from('subscription_plans')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    console.error('Error fetching subscription plan:', error);
    throw error;
  }

  return data ? toSubscriptionPlanDB(data) : null;
};

// Create new subscription plan
export const createSubscriptionPlan = async (input: CreateSubscriptionPlanInput): Promise<SubscriptionPlanDB> => {
  const { data, error } = await supabase
    .from('subscription_plans')
    .insert([{
      name: input.name,
      tier: input.tier,
      price: input.price,
      price_display: input.price_display,
      description: input.description || null,
      features: input.features,
      lead_limit: input.lead_limit,
      is_recommended: input.is_recommended || false,
      is_active: input.is_active !== undefined ? input.is_active : true,
      duration_days: input.duration_days || 30,
    }])
    .select()
    .single();

  if (error) {
    console.error('Error creating subscription plan:', error);
    toast({
      title: "Error",
      description: error.message || "Failed to create subscription plan",
      variant: "destructive",
    });
    throw error;
  }

  toast({
    title: "Success",
    description: "Subscription plan created successfully",
  });

  return toSubscriptionPlanDB(data);
};

// Update subscription plan
export const updateSubscriptionPlan = async (input: UpdateSubscriptionPlanInput): Promise<SubscriptionPlanDB> => {
  const { id, ...updates } = input;

  const { data, error } = await supabase
    .from('subscription_plans')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating subscription plan:', error);
    toast({
      title: "Error",
      description: error.message || "Failed to update subscription plan",
      variant: "destructive",
    });
    throw error;
  }

  toast({
    title: "Success",
    description: "Subscription plan updated successfully",
  });

  return toSubscriptionPlanDB(data);
};

// Delete subscription plan (soft delete by deactivating)
export const deleteSubscriptionPlan = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from('subscription_plans')
    .update({ is_active: false })
    .eq('id', id);

  if (error) {
    console.error('Error deleting subscription plan:', error);
    toast({
      title: "Error",
      description: error.message || "Failed to delete subscription plan",
      variant: "destructive",
    });
    throw error;
  }

  toast({
    title: "Success",
    description: "Subscription plan deactivated successfully",
  });
};

// Toggle plan recommendation (ensure only one plan is recommended at a time)
export const togglePlanRecommendation = async (id: string, isRecommended: boolean): Promise<void> => {
  if (isRecommended) {
    // First, remove recommendation from all other plans
    await supabase
      .from('subscription_plans')
      .update({ is_recommended: false })
      .neq('id', id);
  }

  const { error } = await supabase
    .from('subscription_plans')
    .update({ is_recommended: isRecommended })
    .eq('id', id);

  if (error) {
    console.error('Error toggling recommendation:', error);
    toast({
      title: "Error",
      description: error.message || "Failed to update recommendation",
      variant: "destructive",
    });
    throw error;
  }

  toast({
    title: "Success",
    description: `Plan ${isRecommended ? 'marked as' : 'removed from'} recommended`,
  });
};

// Toggle plan active status
export const togglePlanActive = async (id: string, isActive: boolean): Promise<void> => {
  const { error } = await supabase
    .from('subscription_plans')
    .update({ is_active: isActive })
    .eq('id', id);

  if (error) {
    console.error('Error toggling active status:', error);
    toast({
      title: "Error",
      description: error.message || "Failed to update active status",
      variant: "destructive",
    });
    throw error;
  }

  toast({
    title: "Success",
    description: `Plan ${isActive ? 'activated' : 'deactivated'} successfully`,
  });
};
